// -----------------------------------------------------------
// FloraIQ - Metodologia FloraIQ-LADM (Página Principal)
// -----------------------------------------------------------
// Pagina de documentacao do metodo. Indicadores, registros de exemplo,
// eventos e resultados de validacao eram ficticios e foram retirados; onde
// a tela mostraria dado de execucao, aparece o aviso de dado ausente.

import { useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import {
  LadmOverviewSection,
  LadmMatrixSection,
  LadmFlowSection,
  LadmTerritorialSection,
  LadmSourcesSection,
  LadmRrrSection,
  LadmHistorySection,
  LadmQualitySection,
  LadmProductsSection,
} from "./components/LadmSections";

type TabId = "overview" | "matrix" | "flow" | "territorial" | "sources" | "rrr" | "history" | "quality" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "matrix", label: "Matriz LADM-FloraIQ", icon: "ri-table-line" },
  { id: "flow", label: "Fluxo Operacional", icon: "ri-git-branch-line" },
  { id: "territorial", label: "Vínculos Territoriais", icon: "ri-map-pin-line" },
  { id: "sources", label: "Fontes e Evidências", icon: "ri-folder-info-line" },
  { id: "rrr", label: "RRR Metodológico", icon: "ri-shield-check-line" },
  { id: "history", label: "Histórico", icon: "ri-history-line" },
  { id: "quality", label: "Qualidade", icon: "ri-check-double-line" },
  { id: "reports", label: "Produtos", icon: "ri-file-chart-line" },
];

export default function LadmMethodologyPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <LadmOverviewSection />;
      case "matrix": return <LadmMatrixSection />;
      case "flow": return <LadmFlowSection />;
      case "territorial": return <LadmTerritorialSection />;
      case "sources": return <LadmSourcesSection />;
      case "rrr": return <LadmRrrSection />;
      case "history": return <LadmHistorySection />;
      case "quality": return <LadmQualitySection />;
      case "reports": return <LadmProductsSection />;
      default: return <LadmOverviewSection />;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Metodologia FloraIQ-LADM"
        subtitle="Organize o cadastramento arbóreo como uma base territorial inteligente, relacionando exemplares arbóreos, unidades espaciais, fontes, responsáveis, restrições, eventos e histórico."
      />

      <div className="bg-blue-50/60 border border-blue-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-blue-600"></i>
          </div>
          <p>
            A metodologia FloraIQ-LADM estrutura o inventário arbóreo como uma base georreferenciada, auditável e integrada à gestão territorial,
            permitindo rastrear quem informou, quando informou, qual fonte comprova e qual responsabilidade está vinculada. Esta página descreve o
            método; os indicadores de completude metodológica ainda não são calculados pelo sistema.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-500 bg-blue-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
}
